/**
 * Session-sticky availability latch for the 1:1 contact-chat ("Private") feature.
 *
 * The backend gates every direct-scope endpoint on a server-wide feature switch
 * (`config.ContactChat.Enabled`) and answers 403 "contact chat is not enabled" while it is off —
 * deliberately with NO permission module in front (a private conversation is personal), so a 403
 * from a direct-scope endpoint can only mean the switch is off, never an RBAC denial.
 *
 * Without this latch, a server with the switch off (e.g. staging before the phone-hash backfill)
 * was probed forever: the sidebar's private-unread poll 403'd every minute for every user, and a
 * once-visited Private pane re-403'd on every WS-driven list refresh (frozen workspaces keep the
 * pane alive) — console noise plus a recurring "You don't have permission to do that." toast for
 * a page nobody was on.
 *
 * Semantics: 'unknown' until the first direct-scope response of the session; a success marks
 * 'enabled'; a 403 marks 'disabled' and every direct-scope fetcher short-circuits from then on.
 * Sticky for the session — the switch flips only with a backend redeploy, so a re-probe would
 * need an app restart anyway. Other failures (network, 5xx) never change the status.
 */

/** @type {'unknown' | 'enabled' | 'disabled'} */
let status = "unknown";
const listeners = new Set();

function setStatus(next) {
  if (status === next) return;
  status = next;
  for (const listener of listeners) {
    try {
      listener(status);
    } catch {
      /* a bad listener must not stop the others */
    }
  }
}

/** @returns {'unknown' | 'enabled' | 'disabled'} */
export function getDirectChatAvailability() {
  return status;
}

/** True once a direct-scope request has answered 403 "contact chat is not enabled". */
export function isDirectChatKnownDisabled() {
  return status === "disabled";
}

/** A direct-scope request succeeded — the feature is on. */
export function markDirectChatEnabled() {
  setStatus("enabled");
}

/**
 * Record a failed direct-scope request. Only a 403 (the feature switch) flips the latch;
 * transient failures leave the status untouched so the next tick still probes.
 * @param {{ status?: number } | null | undefined} error
 */
export function markDirectChatDisabledFromError(error) {
  if (error?.status === 403) {
    setStatus("disabled");
  }
}

/**
 * @param {(status: 'unknown' | 'enabled' | 'disabled') => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeDirectChatAvailability(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: reset to the pristine 'unknown' state. */
export function resetDirectChatAvailabilityForTests() {
  setStatus("unknown");
}
